import { useState } from 'react'
import { Link } from 'react-router-dom'
import { RequestStatus } from '@crm/shared'
import type {
  AnalyticsSummary,
  AnalyticsPeriodParams,
  SummaryFilterValues,
  SummaryMetric,
} from '../../../shared/api/analytics.api'
import {
  DeltaBadge,
  fmtInt,
  fmtRub,
  fmtRubShort,
  periodDateRange,
  periodLabel,
} from './summaryFormat'
import { SummaryPeriodClientsModal } from './SummaryPeriodClientsModal'

interface CardDef {
  label: string
  metric: SummaryMetric
  value: string
  title?: string
  hint?: string
  /** Ссылка карточки (список заявок когорты). */
  to?: string | null
  /** Обработчик клика (модалка) — альтернатива ссылке. */
  onClick?: () => void
}

const CARD_CLASS = 'rounded-2xl border border-gray-200 bg-white p-4'
// Только brand-50/500/600/700 — остальные оттенки не генерируются (ui-conventions §1).
const CLICKABLE_CLASS =
  'block w-full text-left transition-colors hover:border-brand-500 hover:shadow-sm cursor-pointer'

function KpiCard({ card }: { card: CardDef }) {
  const body = (
    <>
      <div className="text-xs text-gray-500">{card.label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900 tabular-nums" title={card.title}>
        {card.value}
      </div>
      <div className="mt-1 flex items-center gap-2">
        <DeltaBadge metric={card.metric} />
        {card.hint && <span className="text-[11px] text-gray-400">{card.hint}</span>}
      </div>
    </>
  )
  if (card.to) {
    return (
      <Link to={card.to} className={`${CARD_CLASS} ${CLICKABLE_CLASS}`} title={card.title}>
        {body}
      </Link>
    )
  }
  if (card.onClick) {
    return (
      <button
        onClick={card.onClick}
        className={`${CARD_CLASS} ${CLICKABLE_CLASS}`}
        title={card.title}
      >
        {body}
      </button>
    )
  }
  return <div className={CARD_CLASS}>{body}</div>
}

// Когорта сводки = заявки периода без черновиков — передаём в список полный
// набор статусов, кроме DRAFT, чтобы список совпадал с цифрой карточки.
const NON_DRAFT_STATUSES = Object.values(RequestStatus)
  .filter((s) => s !== 'DRAFT')
  .join(',')

export function SummaryKpi({
  data,
  periodParams,
  filters,
}: {
  data: AnalyticsSummary
  periodParams: AnalyticsPeriodParams
  filters: SummaryFilterValues
}) {
  const [showClients, setShowClients] = useState(false)
  const k = data.kpi

  // Ссылка «когорта периода» в список заявок. Сужение по менеджеру пробрасываем
  // только когда выбран ровно один (список принимает один managerId); остальные
  // фильтры сводки (сегменты/города/бренды/тип) список не умеет — при них цифра
  // карточки может отличаться от списка.
  const range = periodDateRange(periodParams)
  const managerQ = filters.managerIds?.length === 1 ? `&managerId=${filters.managerIds[0]}` : ''
  const cohortTo = range
    ? `/requests?createdFrom=${range.from}&createdTo=${range.to}&statuses=${NON_DRAFT_STATUSES}${managerQ}`
    : null
  const wonTo = range
    ? `/requests?createdFrom=${range.from}&createdTo=${range.to}&statuses=CLOSED_WON${managerQ}`
    : null

  const cards: CardDef[] = [
    {
      label: 'Заявок создано',
      metric: k.requests,
      value: fmtInt(k.requests.current),
      to: cohortTo,
      title: 'Открыть заявки периода',
    },
    {
      label: 'Новых клиентов',
      metric: k.newClients,
      value: fmtInt(k.newClients.current),
      hint: `вернувшихся ${fmtInt(k.returningClients.current)}`,
      onClick: () => setShowClients(true),
      title: 'Показать клиентов периода',
    },
    {
      label: 'Позиций',
      metric: k.items,
      value: fmtInt(k.items.current),
      to: cohortTo,
      title: 'Открыть заявки периода',
    },
    {
      label: 'Сумма КП выставлено',
      metric: k.quotedAmount,
      value: fmtRubShort(k.quotedAmount.current),
      title: fmtRub(k.quotedAmount.current),
      to: cohortTo,
    },
    {
      label: 'Сумма в заказ',
      metric: k.orderedAmount,
      value: fmtRubShort(k.orderedAmount.current),
      title: fmtRub(k.orderedAmount.current),
      to: cohortTo,
    },
    {
      label: 'Конверсия КП→заказ',
      metric: k.conversionRate,
      value: `${k.conversionRate.current}%`,
      hint: 'заявок с КП → в заказ',
      to: wonTo,
      title: 'Открыть заявки периода, ушедшие в заказ',
    },
  ]

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map((c) => (
          <KpiCard key={c.label} card={c} />
        ))}
      </div>
      {showClients && (
        <SummaryPeriodClientsModal
          clients={data.periodClients}
          periodTitle={periodLabel(periodParams)}
          onClose={() => setShowClients(false)}
        />
      )}
    </>
  )
}
